import { Box, CircularProgress, Fade, Grow, type Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { MOBILE_BREAKPOINT } from "../../styles/responsive";

const baseSx: SystemStyleObject<Theme> = {
  position: "absolute",
  // Paper の影は要素の外側に描画されるため、少し広めに覆って影の輪郭が漏れないようにする。
  // 影は主に下方向に落ちるため上は控えめにし、直前の見出し等に白がかぶらないようにする
  // SP は親の幅を超えて横スクロールを出さないよう、横方向には広げない
  inset: { xs: "-8px 0 -24px", [MOBILE_BREAKPOINT]: "-8px -24px -24px" },
  // フォームラベル(zIndex:1)やstickyHeaderの列ヘッダーより上に重ねて、
  // フェードアウトが始まるまで下のコンテンツを完全に隠す
  zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
  // コンテンツ領域と同じ白。薄灰(background.default)だとクロスフェード中に灰色がかって見える
  bgcolor: "background.paper",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2,
  pt: "72px",
};

export const LOADING_EXIT_DURATION_MS = 1400;

const finishedSx: SystemStyleObject<Theme> = {
  // 前半はチェックマークを見せ、後半で透けてコンテンツとクロスフェードする
  // 長さはCSSカスタムプロパティ(--loadingOverlayDuration)経由で渡すため、この定数はレンダーごとに作り直さない
  animation: `loadingOverlayCrossFade var(--loadingOverlayDuration, ${LOADING_EXIT_DURATION_MS}ms) ease-in-out forwards`,
  "@keyframes loadingOverlayCrossFade": {
    "0%": { opacity: 1 },
    "45%": { opacity: 1 },
    "100%": { opacity: 0 },
  },
  // クロスフェード中は下のコンテンツへのクリックを妨げない
  pointerEvents: "none",
};

type LoadingOverlayCssVars = React.CSSProperties & {
  "--loadingOverlayDuration": string;
};

/**
 * ローディング表示と完了の余韻を同じ位置で見せる共通オーバーレイ。
 * `position: relative` な要素の中に置くと、その要素全体を覆う。
 * - 進行中（finished なし）: スピナーを表示
 * - 完了（finished）: スピナーと同じ位置がチェックマーク＋波紋に切り替わり、
 *   後半でフェードアウトして下のコンテンツが透けて現れる（クロスフェード）
 * 進行中と完了で同じコンポーネント・同じ座標系で描くため、
 * 切り替え時の位置合わせは呼び出し側では不要。
 * 表示のオン/オフとタイミングは useLoadingPhase とセットで使う:
 *
 * @example
 * const phase = useLoadingPhase(isLoading, error == null);
 * <Box sx={{ position: "relative" }}>
 *   {phase !== "done" && (
 *     <LoadingOverlay finished={phase === "exit"}>任意の進捗表示</LoadingOverlay>
 *   )}
 *   {phase !== "loading" && コンテンツ}
 * </Box>
 *
 * @param finished 完了の余韻表示に切り替えるか。未指定なら進行中のスピナー表示
 * @param duration クロスフェード全体の長さ（ミリ秒）。useLoadingPhase の exitDuration と合わせる
 * @param children アイコンの下に出す任意の表示（進捗件数など）。不要なら省略
 * @param sx 位置調整などの上書き（例: アイコンの縦位置を変える paddingTop）
 */
export const LoadingOverlay: React.FC<{
  finished?: boolean;
  duration?: number;
  children?: React.ReactNode;
  sx?: SystemStyleObject<Theme>;
}> = ({
  finished = false,
  duration = LOADING_EXIT_DURATION_MS,
  children,
  sx,
}) => {
  const style: LoadingOverlayCssVars = {
    "--loadingOverlayDuration": `${duration}ms`,
  };
  return (
    <Box
      role="status"
      style={style}
      sx={[baseSx, ...(finished ? [finishedSx] : []), ...(sx ? [sx] : [])]}
    >
      {/* スピナーとチェックマークを同じ位置に重ね、
        完了時はスピナーがフェードで消えながらチェックマークが育ってくる */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* チェックマークの背後で波紋が1回広がる（出現タイミングに合わせて遅らせる） */}
        {finished && (
          <Box
            sx={{
              position: "absolute",
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "2px solid",
              borderColor: "success.main",
              opacity: 0,
              animation: "loadingOverlayRipple 0.9s ease-out 0.2s",
              "@keyframes loadingOverlayRipple": {
                "0%": { transform: "scale(0.9)", opacity: 0.5 },
                "100%": { transform: "scale(2)", opacity: 0 },
              },
            }}
          />
        )}
        <Fade in={!finished} timeout={300}>
          <CircularProgress aria-label="読み込み中" />
        </Fade>
        <Grow
          in={finished}
          timeout={400}
          // スピナーが消え始めてから育ち出すよう、少しだけ遅らせる
          style={{ transitionDelay: finished ? "150ms" : "0ms" }}
        >
          <CheckCircleOutlineIcon
            color="success"
            // スピナー(40px)と同じ枠にぴったり重ねる
            sx={{ fontSize: 40, position: "absolute", inset: 0 }}
          />
        </Grow>
      </Box>
      {/* 件数表示等は高頻度更新されるため、role="status" の読み上げ対象から外す */}
      <Box aria-live="off">{children}</Box>
    </Box>
  );
};
